/**
 * Type representing a generic branches configuration
 * Each branch name maps to either a branch identifier or null
 */
type BranchesMap = Record<string, string | null>;

/**
 * Encodes backend branches into URL query parameter format
 * Output format: branches=bom-bff:BB-9001,style-bff:BB-9002
 *
 * @param backendBranches - The backend branches object
 * @returns URL search params string (without leading ?)
 */
const encodeBranchesToUrl = (backendBranches: BranchesMap): string => {
    const activeBranches = Object.entries(backendBranches)
        .filter((entry): entry is [string, string] => {
            const [, branchName] = entry;
            return branchName !== null && branchName !== '';
        })
        .map(([backendName, branchName]) => `${backendName}:${branchName}`);

    if (activeBranches.length === 0) {
        return '';
    }

    return `branches=${activeBranches.join(',')}`;
};

/**
 * Generates a full URL with current backend branches encoded as query parameters
 *
 * @param backendBranches - The backend branches object
 * @returns Full URL with branch parameters
 */
const generateBranchUrl = (backendBranches: BranchesMap): string => {
    const baseUrl = `${window.location.origin}${window.location.pathname}`;
    const existingParams = new URLSearchParams(window.location.search);

    // Remove any existing branches param
    existingParams.delete('branches');

    const branchParams = encodeBranchesToUrl(backendBranches);

    if (!branchParams) {
        // If no branches, return URL with existing params (if any)
        const paramsString = existingParams.toString();
        return paramsString ? `${baseUrl}?${paramsString}` : baseUrl;
    }

    // Combine existing params with branch params
    const allParams = existingParams.toString();
    const separator = allParams ? '&' : '?';

    return `${baseUrl}${allParams ? '?' : ''}${allParams}${separator}${branchParams}`;
};

export { encodeBranchesToUrl, generateBranchUrl };
